import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { ImagePdfViewer, type DocumentItem } from "@onengine/component-library";

import { useFileManager, type FileManagerItem } from "@/components/core/FileManager";
import {
  isPreviewableFile,
  openFileInNewTab,
  toDocumentItem,
} from "@/components/features/FileManager/filePreview.utils";

interface FileManagerPreviewContextValue {
  openPreview: (item: FileManagerItem) => void;
}

const FileManagerPreviewContext = createContext<FileManagerPreviewContextValue | null>(null);

export function useFileManagerPreview(): FileManagerPreviewContextValue {
  const context = useContext(FileManagerPreviewContext);
  if (!context) {
    throw new Error("useFileManagerPreview must be used within FileManagerPreviewProvider");
  }
  return context;
}

export function FileManagerPreviewProvider({ children }: { children: ReactNode }) {
  const { files, resolveDownloadUrl } = useFileManager();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [initialIndex, setInitialIndex] = useState(0);
  const [opened, setOpened] = useState(false);

  const openPreview = useCallback(
    (item: FileManagerItem) => {
      // Every previewable sibling is resolved so the carousel can page through the folder.
      const previewable = files.filter(isPreviewableFile);

      void Promise.all(previewable.map((file) => toDocumentItem(file, resolveDownloadUrl)))
        .then((resolved) => {
          const documentItems = resolved.filter((doc): doc is DocumentItem => doc !== null);
          const index = documentItems.findIndex((doc) => doc.id === item.id);
          if (index < 0) {
            void openFileInNewTab(item, resolveDownloadUrl);
            return;
          }
          setDocuments(documentItems);
          setInitialIndex(index);
          setOpened(true);
        })
        .catch(() => openFileInNewTab(item, resolveDownloadUrl));
    },
    [files, resolveDownloadUrl],
  );

  return (
    <FileManagerPreviewContext.Provider value={{ openPreview }}>
      {children}
      {documents.length > 0 && (
        <ImagePdfViewer
          title={(current) => current.name}
          files={documents}
          opened={opened}
          onClose={() => setOpened(false)}
          initialIndex={initialIndex}
          showThumbnail={false}
        />
      )}
    </FileManagerPreviewContext.Provider>
  );
}
